import React, { useCallback } from 'react';

import { ComplexValue, LoadOptions } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import {
  AddFiltersHandler, DeleteAllFiltersHandler,
  DeleteFilterHandler, UpdateFilterHandler
} from 'app/area/shared/default-filters/types';
import { DefaultFilterValidation } from 'app/area/shared/default-filters/types-store';
import { SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import {
  AddSavedReportsHandler, LoadEvaluationHandler, OnUpdateSavedReport,
  UpdateSavedReportHandler
} from 'app/area/shared/saved-reports/types';

import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { useInvalidSection } from '../metroline/invalid-section';
import { dashboardConfiguration, getDashboardRoute } from '../metroline/dashboard-configuration';
import { DashboardSectionType } from '../metroline/types';
import { DashboardAdminFormState, DashboardItem, DashboardItemValidation } from '../types-store';

import { DashboardFormRenderer } from './dashboard-form-renderer';

interface DashboardFormSectionsProps {
  formState: DashboardAdminFormState;
  favouriteId: number;
  mainLanguage: number;

  saveDashboard: (item: DashboardItem) => void;
  deleteDashboard: () => void;

  updateItem: (item: Partial<DashboardItem>) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  addFilters: AddFiltersHandler;
  updateFilter: UpdateFilterHandler;
  deleteFilter: DeleteFilterHandler;
  deleteAllFilters: DeleteAllFiltersHandler;
  updateFiltersValidation: (validation: { [domain: number]: DefaultFilterValidation }) => void;

  selectCurrentSavedReports: (savedReport: SavedReportDashboard, onlyIdMode: boolean) => void;
  updateCurrentSavedReports: (savedReport: Partial<SavedReportDashboard>) => void;
  updateCurrentValidation: (validation: SavedReportValidation<SavedReportDashboard>) => void;

  loadSavedReportEvaluation: LoadEvaluationHandler<SavedReportDashboard>;

  addSavedReports: AddSavedReportsHandler<SavedReportDashboard>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  updateValidation: (validation: DashboardItemValidation) => void;
  displayError: (errorMessage: string) => void;
  switchDisplayDisabled: (displayDisabled: boolean) => void;

  showTranslations: boolean;
  switchTranslationsToggle: (showTranslations: boolean) => void;

  shouldPrompt: boolean;
  section: string;

  locale: string;
  translation: DashboardAdminTranslation;
}
export const DashboardFormSections: React.FC<DashboardFormSectionsProps> = ({
  formState, favouriteId, mainLanguage, showTranslations,
  saveDashboard, deleteDashboard, shouldPrompt,
  updateItem, changeLanguage, copyLanguageToAll,
  options, loadOptions,
  addFilters, updateFilter, deleteFilter, deleteAllFilters, updateFiltersValidation,
  selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation, loadSavedReportEvaluation,
  addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports,
  updateValidation, displayError, switchDisplayDisabled, switchTranslationsToggle,
  section, locale, translation
}) => {

  const initialSection = section as DashboardSectionType;

  const scrollViewUrl = getDashboardRoute(formState.dashboardItem?.id, favouriteId);

  const configuration = dashboardConfiguration(
    formState.dashboardItem?.id,
    favouriteId,
    formState.validation,
    formState.savedReportsValidation,
    formState.filterValidation
  );

  const [invalidSection, updateInvalidSection] = useInvalidSection(
    configuration, formState.validation, formState.savedReportsValidation, formState.filterValidation);

  const onSaveDashboard = (item: DashboardItem) => {
    updateInvalidSection(undefined);
    saveDashboard(item);
  };

  const loadEvaluation = useCallback((savedReportId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportDashboard>) => {
    const getState = (state: ApplicationState) => state?.dashboardAdministration?.form;
    const onSavedReportError = () => {
      displayError(translation.fields_are_filled_out_incorrectly);
    };
    loadSavedReportEvaluation(formState?.typeId, savedReportId, onUpdateSavedReport, onSavedReportError, getState);
  }, [formState?.typeId]
  );

  return <DashboardFormRenderer
    formState={formState}
    favouriteId={favouriteId}
    mainLanguage={mainLanguage}
    saveDashboard={onSaveDashboard}
    deleteDashboard={deleteDashboard}
    updateItem={updateItem}
    changeLanguage={changeLanguage}
    copyLanguageToAll={copyLanguageToAll}
    options={options}
    loadOptions={loadOptions}
    updateValidation={updateValidation}
    addFilters={addFilters}
    updateFilter={updateFilter}
    deleteFilter={deleteFilter}
    deleteAllFilters={deleteAllFilters}
    updateFiltersValidation={updateFiltersValidation}
    selectCurrentSavedReports={selectCurrentSavedReports}
    updateCurrentSavedReports={updateCurrentSavedReports}
    updateCurrentValidation={updateCurrentValidation}
    loadEvaluation={loadEvaluation}
    addSavedReports={addSavedReports}
    updateSavedReport={updateSavedReport}
    deleteSavedReport={deleteSavedReport}
    deleteAllSavedReports={deleteAllSavedReports}
    displayError={displayError}
    switchDisplayDisabled={switchDisplayDisabled}
    showTranslations={showTranslations}
    switchTranslationsToggle={switchTranslationsToggle}
    locale={locale}
    validation={formState.validation}
    savedReportsValidation={formState.savedReportsValidation}
    translation={translation}
    label={translation.manage_dashboards_title}
    section={section}
    initialSection={initialSection}
    configuration={configuration}
    scrollViewUrl={scrollViewUrl}
    invalidSection={invalidSection}
    shouldPrompt={shouldPrompt}
  />;
};
